import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const AdminTable = ({ collection, columns, statuses }) => {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["admin", collection], queryFn: () => api.get(`/admin/records/${collection}`).then((r) => r.data) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin"] }); };

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/records/${collection}/${id}`, { status }); toast.success("Status updated"); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try { await api.delete(`/admin/records/${collection}/${id}`); toast.success("Deleted"); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };

  if (isLoading) return <p className="text-white/50">Loading…</p>;
  if (!data.length) return <p className="text-white/50 border border-white/10 p-8" data-testid={`admin-${collection}-empty`}>No records yet.</p>;

  return (
    <div className="border border-white/10 overflow-x-auto" data-testid={`admin-${collection}-table`}>
      <Table>
        <TableHeader>
          <TableRow className="border-white/10 hover:bg-transparent">
            {columns.map((c) => <TableHead key={c.key} className="text-[11px] tracking-[0.18em] uppercase text-[#D4AF37]">{c.label}</TableHead>)}
            <TableHead className="text-[11px] tracking-[0.18em] uppercase text-[#D4AF37]">Status</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((r) => (
            <TableRow key={r.id} className="border-white/10 hover:bg-[#2E0F3E]/40" data-testid={`admin-row-${r.id}`}>
              {columns.map((c) => <TableCell key={c.key} className="text-sm text-white/80 max-w-[280px] align-top">{c.render ? c.render(r) : String(r[c.key] ?? "—")}</TableCell>)}
              <TableCell className="align-top">
                <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                  <SelectTrigger className="h-9 w-36 bg-black/50 border-white/20 text-xs" data-testid={`admin-status-select-${r.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s} data-testid={`admin-status-option-${s}`}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </TableCell>
              <TableCell className="align-top">
                <button onClick={() => remove(r.id)} className="text-white/40 hover:text-red-400 transition-colors" data-testid={`admin-delete-${r.id}`} aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
